/**
 * AddBoardDialog — add a Board to an existing wrapper Project, with
 * optional inline upload.
 *
 * Two paths in one dialog:
 *   * Name only → empty Board, no schematic version yet. The user
 *     populates it later via the per-board Upload gestures.
 *   * Name + files → Board minted *and* the upload (netlist + BOM, plus
 *     optional schematic / PCB per the chosen tool) is committed as v1
 *     in one shot.
 *
 * Files are optional overall, but when the user supplies any, the
 * tool's *required* slots (netlist + BOM) must be filled — that's a
 * valid board upload. Auto-suggests `board-{n+1}` based on the existing
 * board count; idempotency on the server means re-using an existing
 * name lands a new version on that board.
 */
import { ToolUploadFields } from "@features/upload/ToolUploadFields";
import {
  type SlotFiles,
  type SlotKey,
  type SourceTool,
  anySlotFilled,
  buildBoardUploadFormData,
  getTool,
  requiredSlotsFilled,
} from "@features/upload/tools";
import { validateSlotFile } from "@features/upload/validate";
import type { Board } from "@models/project";
import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useAddBoard, useUploadProject } from "../../hooks/useProjects";

const NAME_MAX = 256;
const DESCRIPTION_MAX = 280;
const SEMVER_RE = /^\d+\.\d+\.\d+$/;

interface AddBoardDialogProps {
  open: boolean;
  onClose: () => void;
  /** Project that the new board lands under (UUID). */
  projectId: string;
  /** Existing boards on this project — used for the auto-suggested
   *  default name and to communicate context. */
  boards: Board[];
  /** Optional: human-readable project name for the dialog header. */
  projectName?: string;
  /** Owning org id when the project lives under an :Organisation.
   *  Required to route the optional inline upload — the upload
   *  endpoint resolves projects by (project_name, organisation_id). */
  organisationId?: string | null;
  /** Fires after a successful add. Receives the new Board so callers
   *  can route into it / surface a toast. */
  onAdded?: (board: Board) => void;
}

/**
 * Suggest the next free `board-{n+1}` name. Walks existing names so
 * deletions / renames don't produce collisions on the suggestion (e.g.
 * if `board-1` and `board-3` exist, we suggest `board-2`).
 */
function suggestBoardName(boards: Board[]): string {
  const used = new Set(boards.map((b) => b.name.toLowerCase()));
  for (let n = 1; n <= boards.length + 1; n += 1) {
    const candidate = `board-${n}`;
    if (!used.has(candidate)) return candidate;
  }
  return `board-${boards.length + 1}`;
}

export function AddBoardDialog({
  open,
  onClose,
  projectId,
  boards,
  projectName,
  organisationId,
  onAdded,
}: AddBoardDialogProps) {
  const addBoard = useAddBoard();
  const upload = useUploadProject();

  const suggested = useMemo(() => suggestBoardName(boards), [boards]);

  // Identity fields.
  const [name, setName] = useState(suggested);
  const [description, setDescription] = useState("");

  // Optional inline upload — when any slot is filled, the dialog mints
  // the board AND commits a v1 in sequence. When all blank, the board
  // is created empty.
  const [tool, setTool] = useState<SourceTool>("kicad");
  const [files, setFiles] = useState<SlotFiles>({});
  const [slotErrors, setSlotErrors] = useState<
    Partial<Record<SlotKey, string | null>>
  >({});
  const [versionSemver, setVersionSemver] = useState("");

  const [serverError, setServerError] = useState<string | null>(null);
  // Tracks the post-`addBoard` upload phase so the submit button stays
  // pending past the board-create response while the upload ingests.
  const [uploadingV1, setUploadingV1] = useState(false);

  const hasFiles = anySlotFilled(files);
  const uploadReady = requiredSlotsFilled(tool, files);
  const hasSlotErrors = Object.values(slotErrors).some(Boolean);

  // Reseed once per open-transition so the suggestion reflects the
  // current board count and prior selections / errors don't bleed
  // across uses. Deps are intentionally `[open]` ONLY — NOT `suggested`.
  // `suggested` derives from `boards`, which refetches the moment
  // `addBoard` succeeds; depending on it would re-run this reset
  // mid-upload (clearing files + uploadingV1 and calling
  // `upload.reset()`, which detaches the in-flight onSuccess so the
  // dialog never closes and a blanked form invites a stray second
  // board). Capturing the suggestion at open time is exactly what we
  // want.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment — reseed is open-edge-triggered on purpose
  useEffect(() => {
    if (open) {
      setName(suggested);
      setDescription("");
      setTool("kicad");
      setFiles({});
      setSlotErrors({});
      setVersionSemver("");
      setServerError(null);
      setUploadingV1(false);
      addBoard.reset();
      upload.reset();
    }
  }, [open]);

  const trimmedName = name.trim();
  const trimmedSemver = versionSemver.trim();
  const semverInvalid = trimmedSemver !== "" && !SEMVER_RE.test(trimmedSemver);
  // Re-using an existing board name in THIS project is intentional — it
  // lands a new version on that board (the server is idempotent on
  // (project, name)). We surface it so the user knows they're versioning,
  // not creating a second board. Cross-project same names are fine and
  // never collide (the upload targets this board's id).
  const existingBoardWithName =
    trimmedName.length > 0
      ? boards.find((b) => b.name.toLowerCase() === trimmedName.toLowerCase())
      : undefined;

  const pending = addBoard.isPending || uploadingV1;
  // Only surface the "name already exists" note BEFORE submitting. Once
  // submit runs, `addBoard` creates the board and the project list
  // refetches — so mid-upload `boards` already contains the board we're
  // creating, which would otherwise fire a false "already exists" note
  // on a genuine first upload.
  const showExistingNote = !!existingBoardWithName && !pending;
  const canSubmit =
    trimmedName.length > 0 &&
    !pending &&
    !semverInvalid &&
    !hasSlotErrors &&
    (!hasFiles || uploadReady);

  function handleToolChange(next: SourceTool) {
    // Slots differ per tool — drop any staged files / errors so a
    // half-filled KiCad upload can't leak into an EasyEDA submit.
    setTool(next);
    setFiles({});
    setSlotErrors({});
  }

  function handleFile(key: SlotKey, file: File | null) {
    if (file) {
      const slot = getTool(tool).slots.find((s) => s.key === key);
      setSlotErrors((prev) => ({
        ...prev,
        [key]: slot ? validateSlotFile(file, slot) : null,
      }));
    } else {
      setSlotErrors((prev) => ({ ...prev, [key]: null }));
    }
    setFiles((prev) => ({ ...prev, [key]: file }));
  }

  // After the board is minted, if files are present, commit the upload
  // as v1. Errors here are surfaced inline rather than rolling back the
  // board, since the partially-completed board is recoverable (user can
  // retry the upload from the per-board surfaces).
  function uploadV1AfterAdd(board: Board) {
    if (!hasFiles) {
      finalize(board);
      return;
    }

    setUploadingV1(true);
    const fd = buildBoardUploadFormData({
      tool,
      projectName: projectName ?? "",
      boardName: board.name,
      // Target this exact board (addBoard is idempotent on name, so this
      // is the right board whether freshly minted or name-reused). The
      // backend ingests by id — never by name — so a same-named board in
      // another project can't be overwritten.
      boardId: board.id,
      organisationId,
      versionSemver: trimmedSemver,
      files,
    });

    upload.mutate(fd, {
      onSuccess: () => {
        setUploadingV1(false);
        finalize(board);
      },
      onError: (err: unknown) => {
        setUploadingV1(false);
        const msg = err instanceof Error ? err.message : "Upload failed";
        setServerError(
          msg.startsWith("[object")
            ? "Board created, but upload failed — retry from the board's BOM or PCB tab."
            : `Board created, but upload failed: ${msg}`,
        );
        // Don't close — the user can clear the file pickers and try
        // again, or close manually.
      },
    });
  }

  function finalize(board: Board) {
    // Close FIRST so a throwing/navigating `onAdded` (toast, setActiveBoard,
    // route change) can never strand the dialog open after a successful
    // upload.
    onClose();
    onAdded?.(board);
  }

  function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!canSubmit) return;

    addBoard.mutate(
      {
        projectId,
        name: trimmedName,
        descriptionMd: description.trim() || null,
      },
      {
        onSuccess: (board) => {
          uploadV1AfterAdd(board);
        },
        onError: (err) => {
          const msg =
            err instanceof Error ? err.message : "Could not add board";
          setServerError(
            msg.startsWith("[object")
              ? "Could not add board — try a different name."
              : msg,
          );
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: { sx: { width: "100%", maxWidth: 500, m: { xs: 1, sm: 4 } } },
      }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          {projectName ? `Add board to ${projectName}` : "Add board"}
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            disabled={pending}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Add a new board to your project. You can upload files now, or skip
            and upload them later.
          </Typography>

          <TextField
            size="small"
            label="Board name"
            placeholder="e.g., mainboard, sensor, display"
            fullWidth
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: NAME_MAX }}
            helperText={
              showExistingNote
                ? `"${existingBoardWithName?.name}" already exists here — this adds a new version to it (not a new board).`
                : "Re-using an existing name lands a new version on that board."
            }
            FormHelperTextProps={
              showExistingNote ? { sx: { color: "warning.main" } } : undefined
            }
          />

          <TextField
            size="small"
            label="User description"
            placeholder="e.g., USB-C ingress + power management"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
            helperText={`${description.length}/${DESCRIPTION_MAX}`}
          />

          {/* ── Optional inline upload ──────────────────────────── */}
          <Box
            sx={{
              mt: 0.5,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "border.default",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.625rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                color: "text.secondary",
              }}
            >
              Upload (optional)
            </Typography>

            <ToolUploadFields
              tool={tool}
              onToolChange={handleToolChange}
              files={files}
              onFileChange={handleFile}
              errors={slotErrors}
              disabled={pending}
            />

            {hasFiles && !uploadReady && (
              <Typography variant="caption" sx={{ color: "warning.main" }}>
                Netlist and BOM are both required to upload.
              </Typography>
            )}

            <TextField
              size="small"
              label="Version (optional)"
              placeholder="e.g., 1.0.0"
              fullWidth
              value={versionSemver}
              onChange={(e) => setVersionSemver(e.target.value)}
              error={semverInvalid}
              helperText={
                semverInvalid
                  ? "Use major.minor.patch (e.g. 1.0.0)."
                  : "Leave empty to auto-pick v1.0.0."
              }
            />
          </Box>

          {serverError && (
            <Alert severity="error" onClose={() => setServerError(null)}>
              {serverError}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            size="small"
            disabled={pending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={!canSubmit}
            startIcon={
              pending ? <CircularProgress size={14} color="inherit" /> : null
            }
          >
            {pending
              ? uploadingV1
                ? "Uploading…"
                : "Adding…"
              : hasFiles
                ? "Add + upload"
                : "Add board"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
